/**
 * Messages Context
 *
 * Stores messages per agent session for the chat view.
 * Ported from V1 with adaptations for V2's agent model.
 *
 * @module contexts/MessagesContext
 */

import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';

const MessagesContext = createContext(null);

export function MessagesProvider({ children }) {
  // Messages stored separately per agent session for performance
  const [sessionMessages, setSessionMessages] = useState({});

  // Throttled streaming update refs
  const pendingStreamUpdates = useRef({});
  const streamUpdateTimers = useRef({});

  // Current assistant message refs keyed by agent ID (for streaming)
  const currentAssistantMessages = useRef({});

  // Add message to agent session
  const addMessage = useCallback((agentId, message) => {
    setSessionMessages(prev => ({
      ...prev,
      [agentId]: [...(prev[agentId] || []), { ...message, timestamp: Date.now() }],
    }));
  }, []);

  // Update last assistant message (for streaming) - throttled to reduce re-renders
  const updateLastAssistantMessage = useCallback((agentId, content) => {
    // Store the latest content
    pendingStreamUpdates.current[agentId] = content;

    // If no timer is running, start one
    if (!streamUpdateTimers.current[agentId]) {
      streamUpdateTimers.current[agentId] = setTimeout(() => {
        const latestContent = pendingStreamUpdates.current[agentId];
        delete pendingStreamUpdates.current[agentId];
        delete streamUpdateTimers.current[agentId];

        setSessionMessages(prev => {
          const messages = [...(prev[agentId] || [])];

          // Find last assistant message
          for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].type === 'assistant') {
              messages[i] = { ...messages[i], content: latestContent };
              break;
            }
          }

          return {
            ...prev,
            [agentId]: messages,
          };
        });
      }, 50); // Update UI max every 50ms
    }
  }, []);

  // Clear messages for an agent session
  const clearMessages = useCallback((agentId) => {
    setSessionMessages(prev => ({
      ...prev,
      [agentId]: [],
    }));
  }, []);

  // Set messages directly (for history loading)
  const setMessages = useCallback((agentId, messages) => {
    setSessionMessages(prev => ({
      ...prev,
      [agentId]: messages,
    }));
  }, []);

  // Remove messages for an agent session (cleanup)
  const removeSessionMessages = useCallback((agentId) => {
    setSessionMessages(prev => {
      const { [agentId]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Expose currentAssistantMessages ref for WebSocket handler
  const getCurrentAssistantMessage = useCallback((agentId) => {
    return currentAssistantMessages.current[agentId];
  }, []);

  const setCurrentAssistantMessage = useCallback((agentId, content) => {
    if (content === undefined) {
      delete currentAssistantMessages.current[agentId];
    } else {
      currentAssistantMessages.current[agentId] = content;
    }
  }, []);

  const value = useMemo(() => ({
    sessionMessages,
    addMessage,
    updateLastAssistantMessage,
    clearMessages,
    setMessages,
    removeSessionMessages,
    getCurrentAssistantMessage,
    setCurrentAssistantMessage,
  }), [
    sessionMessages,
    addMessage,
    updateLastAssistantMessage,
    clearMessages,
    setMessages,
    removeSessionMessages,
    getCurrentAssistantMessage,
    setCurrentAssistantMessage,
  ]);

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
}

export function useMessages() {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error('useMessages must be used within MessagesProvider');
  }
  return context;
}

// Hook to get messages for a specific agent session only
export function useAgentMessages(agentId) {
  const { sessionMessages } = useMessages();
  return sessionMessages[agentId] || [];
}
